import { useMemo } from 'react';

import { useGetSensorsDataByEventAndIds } from '@realTimeDataAccess';
import {
  CameraChanged,
  type CameraInfo,
  type ISiteControlDeviceChanged,
  type WebRtcStream,
  type WebRtcStreamStatus,
} from '@scaleaq/sitecontrol-lib';

import { findStreamStatus } from '@/utils/streamHealth.functions.ts';

const CAMERA_CHANGED_EVENT = new CameraChanged().className;

type UseLiveStreamStatusArgs = {
  cameraId?: string;
  streamName?: string;
  /** From the REST camera list, which is fetched once; used until the first push arrives. */
  fallbackWebRtcStreams?: WebRtcStream[];
};

/**
 * Stream status kept current by `CameraChanged` pushes. The widget's `camera` prop comes from a
 * one-off REST fetch, so its status would otherwise stay frozen at page load.
 */
export const useLiveStreamStatus = ({
  cameraId,
  streamName,
  fallbackWebRtcStreams,
}: UseLiveStreamStatusArgs): WebRtcStreamStatus | undefined => {
  const id = cameraId ?? '';
  const ids = useMemo(() => [id], [id]);
  const { results } = useGetSensorsDataByEventAndIds<ISiteControlDeviceChanged<CameraInfo>>(
    ids,
    CAMERA_CHANGED_EVENT
  );

  const liveStreams = results?.[id]?.deviceInfo?.webRtcStreams;
  // A push without streams must not blank the status (same guard as NetworkHealthPage).
  return findStreamStatus({
    webRtcStreams: liveStreams?.length ? liveStreams : fallbackWebRtcStreams,
    streamName,
  });
};
